import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PostTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import { IPostTournament } from "api/rpc/2022-09/facilityAdmin/tournament/tournament";
import { formatDate, handleChangeEventInput } from "helpers/Helpers";
import { Select } from "components/select/index";
import Divider from "components/divider";
import { ButtonNew as Button } from "components/buttonNew";
import TextEditor from "components/textEditor/textEditor";

type TTournamentTypes = "regular" | "shotgun" | "crossover";

export default function TournamentNew() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const { t } = useTranslation();

  const [tournament, setTournament] = useState<IPostTournament>({
    name: "",
    start_time: "12:00",
    date: new Date(),
    end_date: new Date(),
    tournament_type: "regular",
    description: "",
  });

  async function createTournament() {
    if (tournament.date) {
      const year = tournament.date.toLocaleString("en-CA", { year: "numeric" });
      const month = tournament.date.toLocaleString("en-CA", { month: "2-digit" });
      const day = tournament.date.toLocaleString("en-CA", { day: "2-digit" });

      const dateString = `${year}-${month}-${day}`;

      const tournamentRes = await PostTournament(
        {
          name: tournament.name,
          start_time: tournament.start_time,
          date: dateString,
          end_date: formatDate(tournament?.end_date),
          description: tournament.description,
          tournament_type: tournament.tournament_type,
        },
        true,
      );

      if (tournamentRes.status !== StatusCode.OK) {
        dispatch(
          showError(
            typeof tournamentRes.data === "string"
              ? tournamentRes.data
              : t("secure.facility.tournament.tournament_new.009"),
          ),
        );
        return;
      }

      dispatch(showSuccess(t("secure.facility.tournament.tournament_new.011")));
      history.push("/admin/events-tournament");
    }
  }

  function handleDateChange(updatedDate: Date) {
    setTournament(prevState => ({ ...prevState, date: updatedDate }));
  }

  function handleTextChange(description: string) {
    const descriptionMaxLength = 251;
    if(description.length === descriptionMaxLength) {
      return;
    }
    setTournament(prevState => ({...prevState, description: description}));
  }

  function handleNavigateBack(){
    history.push("/admin/events-tournament");
  }

  return (
    <Page
      title={t("Create Tournament")}
      subtitle={t('Enter the key details for your tournament')}
    >
      <Divider /> 
      <Form>
        <Card title={t("secure.facility.tournament.tournament_new.004")} >
          <Divider/>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={tournament.name}
                  label={t("secure.facility.tournament.tournament_new.005")}
                  id="name"
                  onChange={e => handleChangeEventInput(e, tournament, setTournament)}
                  placeholder={t("Tournament Name")}
                />

                <Select
                  smallDropdownSize
                  onChange={(val: TTournamentTypes) => setTournament(prev => ({ ...prev, tournament_type: val }))}
                  defaultValue={tournament.tournament_type}
                  label={t("Tournament Type")}
                >
                  {tournamentTypes().map((type, index: number) => {
                    return (
                      <Option key={index} value={type.value} name={type.label}>
                        <span>{t(`${type.label}`)}</span>
                      </Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
            </FormLayout>
            <FormLayout>
              <FormLayout.Group>
                <TimePick
                  value={tournament.start_time}
                  onChange={timeString => setTournament(prev => ({ ...prev, start_time: timeString }))}
                  label={t("secure.facility.tournament.tournament_new.007")}
                  size="large"
                  leftAlignedClock
                  status={tournament.start_time === undefined ? "warning" : undefined}
                />
              </FormLayout.Group>
              <FormLayout.Group>
              <DatePickerInput
                  label={t("Start date")}
                  months={1}
                  position="left"
                  startingDate={tournament.date}
                  setStartingDate={handleDateChange}
                />
                 <DatePickerInput
                  label={t("End date")}
                  months={1}
                  position="left"
                  startingDate={tournament.end_date}
                  setStartingDate={date => setTournament(prev => ({...prev, end_date: date as Date}))}
                />
              </FormLayout.Group>
            </FormLayout>

            <FormLayout>
              <FormLayout.Group>
                <TextEditor
                  markdownText={tournament.description}
                  markdownTextOnChange={handleTextChange}
                  label={t("Description (Optional)")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
 
        </Card>

        <div className="flex flex-row-reverse gap-2 pt-4">
          <Button
            type="primary"
            onClick={ () => createTournament()}
          >
            {t("Create Tournament")}
          </Button>

          <Button
            type="secondary"
            onClick={handleNavigateBack}
          >
            {t("Cancel")}
          </Button>
        </div>
      </Form>
    </Page>
  );
  
}

export const tournamentTypes = () : { label: string; value: TTournamentTypes}[] => {
  return [
    { label: "Regular", value: "regular" },
    { label: "Shotgun", value: "shotgun" },
    { label: "Crossover", value: "crossover" },
  ];
};